<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email, sameAs } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import SimpleDivider from '../../../../../components/Divider/SimpleDivider.vue';
import FormInput from '../../../../../components/Form/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { isValidPassword } from 'shared/helpers/Validators';
import GoogleOAuthButton from '../../../../../components/GoogleOauth/Button.vue';
import { register } from '../../../../../api/auth';
import CountrySelect from './CountrySelect.vue';

const MIN_PASSWORD_LENGTH = 6;
const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{}|'"/\\.,`<>:;?~]/;

// Os degraus e os preços do pacote de contatos vêm do BACKEND
// (/public/api/v1/signup_lookups/contacts_options): o incluído sai do plano
// padrão e o preço sai da própria AccountSubscription, a mesma que monta a
// fatura. Codificar a tabela aqui já custou receita — a tela prometia R$188
// por "5.000" enquanto o plano já incluía 5.000 e a fatura saía R$0.
const CONTACTS_OPTIONS_URL = '/public/api/v1/signup_lookups/contacts_options';

const onlyDigits = v => (v || '').toString().replace(/\D/g, '');

const isValidCPF = value => {
  const cpf = onlyDigits(value);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const digit = pos => {
    let sum = 0;
    for (let i = 0; i < pos; i += 1) sum += Number(cpf[i]) * (pos + 1 - i);
    const rest = ((sum * 10) % 11) % 10;
    return rest === Number(cpf[pos]);
  };
  return digit(9) && digit(10);
};

const isValidCNPJ = value => {
  const cnpj = onlyDigits(value);
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
  const calc = len => {
    const weights =
      len === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < len; i += 1) sum += Number(cnpj[i]) * weights[i];
    const rest = sum % 11;
    return (rest < 2 ? 0 : 11 - rest) === Number(cnpj[len]);
  };
  return calc(12) && calc(13);
};

export default {
  components: {
    CountrySelect,
    FormInput,
    GoogleOAuthButton,
    NextButton,
    SimpleDivider,
    Icon,
    VueHcaptcha,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      credentials: {
        accountName: '',
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        hCaptchaClientResponse: '',
        // Código de indicação da revenda, lido de ?ref= na URL.
        ref: new URLSearchParams(window.location.search).get('ref') || '',
        // Dados de cobrança (obrigatórios p/ gerar a fatura da assinatura).
        // País define o resto do formulário: fora do BR não há CPF/CNPJ nem CEP,
        // e a cobrança é combinada com o comercial (sem PIX/boleto).
        country: 'BR',
        // Idioma escolhido na tela: muda a interface na hora e a conta nasce
        // nele (o dashboard abre no idioma do cliente, não no nosso).
        locale: 'pt_BR',
        personType: 'cnpj', // 'cpf' (pessoa) | 'cnpj' (empresa)
        document: '',
        phone: '',
        postalCode: '',
        address: '',
        addressNumber: '',
        complement: '',
        province: '',
        city: '',
        state: '',
      },
      cepLoading: false,
      docLoading: false,
      didCaptchaReset: false,
      isSignupInProgress: false,
      error: '',
      // Vazio até o backend responder. Se a chamada falhar o bloco some da
      // tela e o cadastro segue sem escolher pacote — a conta fica com o
      // incluído do plano, que é o padrão seguro (nunca cobra a mais nem
      // rebaixa o limite).
      contactsPlan: null,
      contactOptions: [],
      selectedContacts: null,
      // Passo 1 = monta o plano, passo 2 = preenche os dados. O cliente vê o
      // preço ANTES de digitar dez campos.
      step: 1,
      pricing: null,
      sellableModules: [],
      extras: { inbox: 0, agent: 0, storage: 0 },
      selectedModules: [],
    };
  },
  validations() {
    return {
      credentials: {
        accountName: {
          required,
          minLength: minLength(2),
        },
        fullName: {
          required,
          minLength: minLength(2),
        },
        email: {
          required,
          email,
        },
        password: {
          required,
          isValidPassword,
          minLength: minLength(MIN_PASSWORD_LENGTH),
        },
        confirmPassword: {
          required,
          minLength: minLength(MIN_PASSWORD_LENGTH),
          sameAsPassword: sameAs(this.credentials.password),
        },
        // Fora do Brasil o documento é um VAT/EIN de formato desconhecido:
        // exigir e validar travaria o cadastro. Bairro, número e estado também
        // não existem em todo lugar — sobra o essencial para faturar depois.
        document: this.isBrazil
          ? {
              required,
              validDocument: value =>
                this.credentials.personType === 'cpf'
                  ? isValidCPF(value)
                  : isValidCNPJ(value),
            }
          : {},
        phone: { required, minLength: minLength(8) },
        postalCode: this.isBrazil ? { required, minLength: minLength(8) } : {},
        address: { required },
        addressNumber: this.isBrazil ? { required } : {},
        province: this.isBrazil ? { required } : {},
        city: { required },
        state: this.isBrazil ? { required } : {},
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    isBrazil() {
      return this.credentials.country === 'BR';
    },
    showContactsPicker() {
      return this.contactOptions.length > 0 && !!this.selectedContacts;
    },
    // Sem catálogo do backend não há passo 1: o cadastro cai direto nos dados e
    // a conta nasce com o incluído do plano. Nunca inventar preço na tela.
    showPlanBuilder() {
      return !!this.pricing && this.showContactsPicker;
    },
    extrasTotal() {
      if (!this.pricing) return 0;
      return (
        this.extras.inbox * this.pricing.inbox_price +
        this.extras.agent * this.pricing.agent_price +
        this.extras.storage * this.pricing.storage_block_price
      );
    },
    // Linhas de recurso avulso do passo 1. Rótulo e preço saem do backend; a
    // quantidade é SEMPRE "acima do incluído", nunca o total.
    extraResourceRows() {
      if (!this.pricing) return [];
      return [
        {
          key: 'inbox',
          label: this.$t('REGISTER.PLAN.EXTRA_INBOX'),
          price: this.pricing.inbox_price,
        },
        {
          key: 'agent',
          label: this.$t('REGISTER.PLAN.EXTRA_AGENT'),
          price: this.pricing.agent_price,
        },
        {
          key: 'storage',
          label: this.$t('REGISTER.PLAN.EXTRA_STORAGE'),
          price: this.pricing.storage_block_price,
        },
      ];
    },
    modulesTotal() {
      return this.sellableModules
        .filter(m => this.selectedModules.includes(m.slug))
        .reduce((sum, m) => sum + m.monthly_price, 0);
    },
    // Todos os precos vem do backend (pricing/options/modules). Nenhum literal
    // aqui: foi a tabela chumbada em JS que fez a tela prometer um valor que a
    // fatura nao cobrava.
    monthlyTotal() {
      const base = this.contactsPlan?.monthly_price || 0;
      const contacts = this.selectedContacts?.price || 0;
      return base + contacts + this.extrasTotal + this.modulesTotal;
    },
    localeOptions() {
      return [
        { id: 'pt_BR', label: 'Português' },
        { id: 'en', label: 'English' },
        { id: 'es', label: 'Español' },
      ];
    },
    termsLink() {
      return this.$t('REGISTER.TERMS_ACCEPT')
        .replace('https://www.chatwoot.com/terms', this.globalConfig.termsURL)
        .replace(
          'https://www.chatwoot.com/privacy-policy',
          this.globalConfig.privacyURL
        );
    },
    hasAValidCaptcha() {
      if (this.globalConfig.hCaptchaSiteKey) {
        return !!this.credentials.hCaptchaClientResponse;
      }
      return true;
    },
    confirmPasswordErrorText() {
      const { confirmPassword } = this.v$.credentials;
      if (!confirmPassword.$error) return '';
      if (confirmPassword.sameAsPassword.$invalid) {
        return this.$t('REGISTER.CONFIRM_PASSWORD.ERROR');
      }
      return '';
    },
    showGoogleOAuth() {
      // Cadastro pelo Google desativado de propósito: senão a empresa nasceria
      // como "empresa gmail". Cadastro é só por e-mail corporativo.
      return false;
    },
    isFormValid() {
      return !this.v$.$invalid && this.hasAValidCaptcha;
    },
    passwordRequirements() {
      const password = this.credentials.password || '';
      return {
        length: password.length >= MIN_PASSWORD_LENGTH,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        special: SPECIAL_CHAR_REGEX.test(password),
      };
    },
    passwordRequirementItems() {
      const reqs = this.passwordRequirements;
      return [
        {
          id: 'length',
          met: reqs.length,
          label: this.$t('REGISTER.PASSWORD.REQUIREMENTS_LENGTH', {
            min: MIN_PASSWORD_LENGTH,
          }),
        },
        {
          id: 'uppercase',
          met: reqs.uppercase,
          label: this.$t('REGISTER.PASSWORD.REQUIREMENTS_UPPERCASE'),
        },
        {
          id: 'lowercase',
          met: reqs.lowercase,
          label: this.$t('REGISTER.PASSWORD.REQUIREMENTS_LOWERCASE'),
        },
        {
          id: 'number',
          met: reqs.number,
          label: this.$t('REGISTER.PASSWORD.REQUIREMENTS_NUMBER'),
        },
        {
          id: 'special',
          met: reqs.special,
          label: this.$t('REGISTER.PASSWORD.REQUIREMENTS_SPECIAL'),
        },
      ];
    },
    passwordRequirementsMet() {
      return Object.values(this.passwordRequirements).every(Boolean);
    },
  },
  mounted() {
    this.fetchContactsOptions();
  },
  methods: {
    // Troca o idioma da tela na hora; o mesmo valor vai no cadastro.
    changeLocale(locale) {
      this.credentials.locale = locale;
      if (window.i18n) window.i18n.global.locale.value = locale;
    },
    // Degraus + preços do pacote de contatos, servidos pelo backend. Falhou?
    // O bloco simplesmente não aparece e o cadastro segue com o incluído do
    // plano — melhor não oferecer do que oferecer um preço inventado.
    async fetchContactsOptions() {
      try {
        const res = await fetch(CONTACTS_OPTIONS_URL);
        if (!res.ok) return;
        const data = await res.json();
        if (!Array.isArray(data.options) || !data.options.length) return;
        this.contactsPlan = data.plan || null;
        this.contactOptions = data.options;
        [this.selectedContacts] = data.options;
        this.pricing = data.pricing || null;
        this.sellableModules = Array.isArray(data.modules) ? data.modules : [];
      } catch (e) {
        // silencioso: o bloco fica oculto
      }
    },
    formatNumber(value) {
      return new Intl.NumberFormat(
        this.credentials.locale === 'en' ? 'en-US' : 'pt-BR'
      ).format(value || 0);
    },
    formatMoney(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value || 0);
    },
    // Rótulo do degrau: o número vem do backend, o texto do i18n.
    contactsOptionLabel(option) {
      if (option.unlimited) return this.$t('REGISTER.CONTACTS.UNLIMITED');
      const amount = this.formatNumber(option.limit);
      return option.key === 'INCLUDED'
        ? this.$t('REGISTER.CONTACTS.INCLUDED', { amount })
        : this.$t('REGISTER.CONTACTS.PACK', { amount });
    },
    // Preço mensal SÓ da linha de contatos (R$ 0 = sem custo extra).
    formatContactsPrice(price) {
      return price === 0
        ? '—'
        : `${this.formatMoney(price)}${this.$t('REGISTER.CONTACTS.PER_MONTH')}`;
    },
    goToDataStep() {
      this.step = 2;
    },
    bumpExtra(key, delta) {
      this.extras[key] = Math.max(0, this.extras[key] + delta);
    },
    toggleModule(slug) {
      const at = this.selectedModules.indexOf(slug);
      if (at === -1) this.selectedModules.push(slug);
      else this.selectedModules.splice(at, 1);
    },
    async submit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        this.resetCaptcha();
        return;
      }
      this.isSignupInProgress = true;
      try {
        await register({
          ...this.credentials,
          // Pacote de contatos escolhido no cadastro. Os dois campos vêm do
          // backend e são NULOS na opção "incluído" — mandar o número do plano
          // ali gravava custom_limits e rebaixava o limite da conta.
          contactsTier: this.selectedContacts?.contacts_tier,
          contactsLimit: this.selectedContacts?.contacts_limit,
          // Recursos extras: só mandamos quando o cliente pediu MAIS que o
          // incluído. Zero extra = campo ausente = conta fica com o do plano.
          inboxLimit: this.extras.inbox
            ? (this.contactsPlan?.inbox_included || 0) + this.extras.inbox
            : undefined,
          agentLimit: this.extras.agent
            ? (this.contactsPlan?.agent_included || 0) + this.extras.agent
            : undefined,
          storageMb: this.extras.storage
            ? (this.contactsPlan?.storage_included_mb || 0) +
              this.extras.storage * (this.pricing?.storage_block_mb || 0)
            : undefined,
          modules: this.selectedModules.length
            ? this.selectedModules
            : undefined,
        });
        window.location = DEFAULT_REDIRECT_URL;
      } catch (error) {
        let errorMessage =
          error?.message || this.$t('REGISTER.API.ERROR_MESSAGE');
        this.resetCaptcha();
        useAlert(errorMessage);
      } finally {
        this.isSignupInProgress = false;
      }
    },
    // CEP → endereço (nosso endpoint público, viacep por trás).
    async lookupCep() {
      if (!this.isBrazil) return;
      const cep = onlyDigits(this.credentials.postalCode);
      if (cep.length !== 8) return;
      this.cepLoading = true;
      try {
        const res = await fetch(`/public/api/v1/signup_lookups/cep?cep=${cep}`);
        if (res.ok) {
          const d = await res.json();
          this.credentials.address = d.address || this.credentials.address;
          this.credentials.province = d.province || this.credentials.province;
          this.credentials.city = d.city || this.credentials.city;
          this.credentials.state = d.state || this.credentials.state;
        }
      } catch (e) {
        // silencioso; o usuário preenche manualmente
      } finally {
        this.cepLoading = false;
      }
    },
    // CPF → nome | CNPJ → razão social (nosso CpfCnpjClient). Só chama se o
    // documento for válido (evita gastar consulta paga com lixo).
    async lookupDocument() {
      if (!this.isBrazil) return;
      const doc = onlyDigits(this.credentials.document);
      const valid =
        this.credentials.personType === 'cpf'
          ? isValidCPF(doc)
          : isValidCNPJ(doc);
      if (!valid) return;
      this.docLoading = true;
      try {
        const res = await fetch(
          `/public/api/v1/signup_lookups/document?document=${doc}`
        );
        if (res.ok) {
          const d = await res.json();
          // CNPJ: razão social → empresa; nome do decisor → nome.
          // CPF: nome → nome (e vira também o nome da conta). Telefone preenche.
          if (this.credentials.personType === 'cnpj') {
            if (d.company_name) this.credentials.accountName = d.company_name;
            if (d.name) this.credentials.fullName = d.name;
          } else if (d.name) {
            this.credentials.fullName = d.name;
            if (!this.credentials.accountName) {
              this.credentials.accountName = d.name;
            }
          }
          if (d.phone && !this.credentials.phone) {
            this.credentials.phone = d.phone;
          }
        }
      } catch (e) {
        // silencioso
      } finally {
        this.docLoading = false;
      }
    },
    onRecaptchaVerified(token) {
      this.credentials.hCaptchaClientResponse = token;
      this.didCaptchaReset = false;
      this.v$.$touch();
    },
    resetCaptcha() {
      if (!this.globalConfig.hCaptchaSiteKey) return;
      this.$refs.hCaptcha.reset();
      this.credentials.hCaptchaClientResponse = '';
      this.didCaptchaReset = true;
    },
  },
};
</script>

<template>
  <div class="flex-1 px-1 overflow-auto">
    <form class="space-y-3" @submit.prevent="submit">
      <!-- Idioma da tela (e da conta que vai nascer) -->
      <div class="flex gap-1 p-1 border rounded-lg border-n-weak">
        <button
          v-for="opt in localeOptions"
          :key="opt.id"
          type="button"
          class="flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors"
          :class="
            credentials.locale === opt.id
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-11'
          "
          @click="changeLocale(opt.id)"
        >
          {{ opt.label }}
        </button>
      </div>

      <!-- PASSO 1 — monta o plano. O cliente vê o total antes de digitar. -->
      <section v-if="showPlanBuilder && step === 1" class="flex flex-col gap-3">
        <div class="flex items-baseline justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ contactsPlan.name }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ formatMoney(contactsPlan.monthly_price) }}
          </span>
        </div>
        <p class="text-xs text-n-slate-10">
          {{
            $t('REGISTER.PLAN.INCLUDED_SUMMARY', {
              inboxes: contactsPlan.inbox_included,
              agents: contactsPlan.agent_included,
              contacts: formatNumber(contactsPlan.contacts_included),
            })
          }}
        </p>

        <fieldset class="flex flex-col gap-2">
          <legend class="text-sm font-medium text-n-slate-12">
            {{ $t('REGISTER.CONTACTS.TITLE') }}
          </legend>
          <label
            v-for="option in contactOptions"
            :key="option.id"
            class="flex items-center justify-between px-3 py-2 border rounded-lg cursor-pointer border-n-weak"
            :class="{
              'border-n-brand bg-n-alpha-2': selectedContacts.id === option.id,
            }"
          >
            <span class="flex items-center gap-2 text-sm">
              <input
                v-model="selectedContacts"
                type="radio"
                name="contacts"
                :value="option"
              />
              <span>{{ contactsOptionLabel(option) }}</span>
            </span>
            <span class="text-sm font-medium">{{
              formatMoney(option.price)
            }}</span>
          </label>
        </fieldset>

        <div
          v-for="extra in extraResourceRows"
          :key="extra.key"
          class="flex items-center justify-between px-3 py-2 border rounded-lg border-n-weak"
        >
          <span class="text-sm">{{ extra.label }}</span>
          <span class="flex items-center gap-3">
            <button
              type="button"
              class="w-6 h-6 rounded border border-n-weak text-n-slate-11"
              @click="bumpExtra(extra.key, -1)"
            >
              −
            </button>
            <span class="w-4 text-sm text-center">{{ extras[extra.key] }}</span>
            <button
              type="button"
              class="w-6 h-6 rounded border border-n-weak text-n-slate-11"
              @click="bumpExtra(extra.key, 1)"
            >
              +
            </button>
            <span class="w-20 text-sm font-medium text-right">
              {{ formatMoney(extras[extra.key] * extra.price) }}
            </span>
          </span>
        </div>

        <fieldset v-if="sellableModules.length" class="flex flex-col gap-2">
          <legend class="text-sm font-medium text-n-slate-12">
            {{ $t('REGISTER.PLAN.MODULES') }}
          </legend>
          <label
            v-for="mod in sellableModules"
            :key="mod.slug"
            class="flex items-center justify-between px-3 py-2 border rounded-lg cursor-pointer border-n-weak"
            :class="{
              'border-n-brand bg-n-alpha-2': selectedModules.includes(mod.slug),
            }"
          >
            <span class="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                :checked="selectedModules.includes(mod.slug)"
                @change="toggleModule(mod.slug)"
              />
              <span>{{ mod.name }}</span>
            </span>
            <span class="text-sm font-medium">{{
              formatMoney(mod.monthly_price)
            }}</span>
          </label>
        </fieldset>

        <div
          class="flex items-baseline justify-between pt-2 border-t border-n-weak"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('REGISTER.PLAN.TOTAL') }}
          </span>
          <span class="text-lg font-semibold text-n-slate-12">
            {{ formatMoney(monthlyTotal) }}
          </span>
        </div>
        <NextButton
          lg
          type="button"
          class="w-full"
          icon="i-lucide-chevron-right"
          trailing-icon
          :label="$t('REGISTER.PLAN.CONTINUE')"
          @click="goToDataStep"
        />
      </section>

      <!-- PASSO 2 — os dados. v-show (não v-if) para não remontar os campos e
           perder o estado do vuelidate ao voltar. -->
      <div v-show="!showPlanBuilder || step === 2" class="space-y-3">
        <!-- País: define o resto do formulário -->
        <div>
          <CountrySelect
            v-model="credentials.country"
            :label="$t('REGISTER.COUNTRY.LABEL')"
            :placeholder="$t('REGISTER.COUNTRY.SEARCH')"
            :empty-text="$t('REGISTER.COUNTRY.EMPTY')"
          />
          <p v-if="!isBrazil" class="mt-1 text-xs text-n-slate-10">
            {{ $t('REGISTER.COUNTRY.HINT') }}
          </p>
        </div>

        <!-- Tipo de pessoa -->
        <div class="flex gap-2">
          <button
            v-for="opt in [
              {
                id: 'cnpj',
                label: isBrazil
                  ? $t('REGISTER.PERSON_TYPE.COMPANY')
                  : $t('REGISTER.PERSON_TYPE.COMPANY_INTL'),
              },
              {
                id: 'cpf',
                label: isBrazil
                  ? $t('REGISTER.PERSON_TYPE.PERSON')
                  : $t('REGISTER.PERSON_TYPE.PERSON_INTL'),
              },
            ]"
            :key="opt.id"
            type="button"
            class="flex-1 px-3 py-2 text-sm font-medium border rounded-lg transition-colors"
            :class="
              credentials.personType === opt.id
                ? 'border-n-brand text-n-slate-12 bg-n-alpha-2'
                : 'border-n-weak text-n-slate-11'
            "
            @click="credentials.personType = opt.id"
          >
            {{ opt.label }}
          </button>
        </div>

        <!-- Documento primeiro: buscamos nome/empresa e telefone por ele -->
        <div>
          <FormInput
            v-model="credentials.document"
            name="document"
            :label="
              isBrazil
                ? credentials.personType === 'cpf'
                  ? 'CPF'
                  : 'CNPJ'
                : $t('REGISTER.TAX_ID.LABEL')
            "
            :placeholder="
              isBrazil
                ? credentials.personType === 'cpf'
                  ? '000.000.000-00'
                  : '00.000.000/0000-00'
                : ''
            "
            :has-error="v$.credentials.document.$error"
            :error-message="
              credentials.personType === 'cpf'
                ? 'CPF inválido'
                : 'CNPJ inválido'
            "
            @blur="
              v$.credentials.document.$touch();
              lookupDocument();
            "
          />
          <p class="mt-1 text-xs text-n-slate-10">
            <span v-if="docLoading">
              {{ $t('REGISTER.DOCUMENT.LOOKUP_LOADING') }}
            </span>
            <span v-else-if="!isBrazil">
              {{ $t('REGISTER.TAX_ID.HINT') }}
            </span>
            <span v-else>
              {{
                credentials.personType === 'cpf'
                  ? $t('REGISTER.DOCUMENT.HINT_CPF')
                  : $t('REGISTER.DOCUMENT.HINT_CNPJ')
              }}
            </span>
          </p>
        </div>

        <!-- Nome + empresa (preenchidos pela consulta; pode ajustar) -->
        <div class="grid grid-cols-2 gap-2">
          <FormInput
            v-model="credentials.fullName"
            name="full_name"
            class="flex-1"
            :class="{ error: v$.credentials.fullName.$error }"
            :label="$t('REGISTER.FULL_NAME.LABEL')"
            :placeholder="$t('REGISTER.FULL_NAME.PLACEHOLDER')"
            :has-error="v$.credentials.fullName.$error"
            :error-message="$t('REGISTER.FULL_NAME.ERROR')"
            @blur="v$.credentials.fullName.$touch"
          />
          <FormInput
            v-model="credentials.accountName"
            name="account_name"
            class="flex-1"
            :class="{ error: v$.credentials.accountName.$error }"
            :label="$t('REGISTER.COMPANY_NAME.LABEL')"
            :placeholder="$t('REGISTER.COMPANY_NAME.PLACEHOLDER')"
            :has-error="v$.credentials.accountName.$error"
            :error-message="$t('REGISTER.COMPANY_NAME.ERROR')"
            @blur="v$.credentials.accountName.$touch"
          />
        </div>

        <FormInput
          v-model="credentials.email"
          type="email"
          name="email_address"
          :class="{ error: v$.credentials.email.$error }"
          :label="$t('REGISTER.EMAIL.LABEL')"
          :placeholder="$t('REGISTER.EMAIL.PLACEHOLDER')"
          :has-error="v$.credentials.email.$error"
          :error-message="$t('REGISTER.EMAIL.ERROR')"
          @blur="v$.credentials.email.$touch"
        />

        <FormInput
          v-model="credentials.phone"
          name="phone"
          :label="$t('REGISTER.PHONE.LABEL')"
          :placeholder="
            isBrazil
              ? $t('REGISTER.PHONE.PLACEHOLDER')
              : $t('REGISTER.PHONE.PLACEHOLDER_INTL')
          "
          :has-error="v$.credentials.phone.$error"
          :error-message="$t('REGISTER.PHONE.ERROR')"
          @blur="v$.credentials.phone.$touch"
        />

        <!-- CEP + endereço -->
        <div class="grid grid-cols-[8rem_1fr] gap-2">
          <FormInput
            v-model="credentials.postalCode"
            name="postal_code"
            :label="
              isBrazil
                ? $t('REGISTER.ADDRESS.POSTAL_CODE')
                : $t('REGISTER.ADDRESS.POSTAL_CODE_INTL')
            "
            :placeholder="isBrazil ? '00000-000' : ''"
            :has-error="v$.credentials.postalCode.$error"
            :error-message="$t('REGISTER.ADDRESS.POSTAL_CODE_ERROR')"
            @blur="
              v$.credentials.postalCode.$touch();
              lookupCep();
            "
          />
          <FormInput
            v-model="credentials.address"
            name="address"
            :label="$t('REGISTER.ADDRESS.STREET')"
            :placeholder="$t('REGISTER.ADDRESS.STREET_PLACEHOLDER')"
            :has-error="v$.credentials.address.$error"
            :error-message="$t('REGISTER.ADDRESS.STREET_ERROR')"
            @blur="v$.credentials.address.$touch"
          />
        </div>
        <div class="grid grid-cols-3 gap-2">
          <FormInput
            v-model="credentials.addressNumber"
            name="address_number"
            :label="$t('REGISTER.ADDRESS.NUMBER')"
            placeholder="123"
            :has-error="v$.credentials.addressNumber.$error"
            :error-message="$t('REGISTER.ADDRESS.NUMBER')"
            @blur="v$.credentials.addressNumber.$touch"
          />
          <FormInput
            v-model="credentials.complement"
            name="complement"
            :label="$t('REGISTER.ADDRESS.COMPLEMENT')"
            :placeholder="$t('REGISTER.ADDRESS.COMPLEMENT_PLACEHOLDER')"
          />
          <FormInput
            v-if="isBrazil"
            v-model="credentials.province"
            name="province"
            :label="$t('REGISTER.ADDRESS.DISTRICT')"
            :placeholder="$t('REGISTER.ADDRESS.DISTRICT')"
            :has-error="v$.credentials.province.$error"
            :error-message="$t('REGISTER.ADDRESS.DISTRICT')"
            @blur="v$.credentials.province.$touch"
          />
        </div>
        <div class="grid grid-cols-[1fr_5rem] gap-2">
          <FormInput
            v-model="credentials.city"
            name="city"
            :label="$t('REGISTER.ADDRESS.CITY')"
            :placeholder="$t('REGISTER.ADDRESS.CITY')"
            :has-error="v$.credentials.city.$error"
            :error-message="$t('REGISTER.ADDRESS.CITY_ERROR')"
            @blur="v$.credentials.city.$touch"
          />
          <FormInput
            v-model="credentials.state"
            name="state"
            :label="
              isBrazil
                ? $t('REGISTER.ADDRESS.STATE')
                : $t('REGISTER.ADDRESS.STATE_INTL')
            "
            :placeholder="isBrazil ? 'UF' : ''"
            :has-error="v$.credentials.state.$error"
            :error-message="$t('REGISTER.ADDRESS.STATE')"
            @blur="v$.credentials.state.$touch"
          />
        </div>

        <FormInput
          v-model="credentials.password"
          type="password"
          name="password"
          :class="{ error: v$.credentials.password.$error }"
          :label="$t('LOGIN.PASSWORD.LABEL')"
          :placeholder="$t('SET_NEW_PASSWORD.PASSWORD.PLACEHOLDER')"
          :has-error="v$.credentials.password.$error"
          aria-describedby="password-requirements"
          @blur="v$.credentials.password.$touch"
        />
        <div
          id="password-requirements"
          class="text-xs rounded-md px-4 py-3 outline outline-1 outline-n-weak bg-n-alpha-black2"
        >
          <ul role="list" class="grid grid-cols-2 gap-1">
            <li
              v-for="item in passwordRequirementItems"
              :key="item.id"
              class="inline-flex gap-1 items-start"
            >
              <Icon
                class="flex-none flex-shrink-0 w-3 mt-0.5"
                :icon="
                  item.met ? 'i-lucide-circle-check-big' : 'i-lucide-circle'
                "
                :class="item.met ? 'text-n-teal-10' : 'text-n-slate-10'"
              />

              <span :class="item.met ? 'text-n-slate-11' : 'text-n-slate-10'">
                {{ item.label }}
              </span>
            </li>
          </ul>
        </div>
        <FormInput
          v-model="credentials.confirmPassword"
          type="password"
          name="confirm_password"
          :class="{ error: v$.credentials.confirmPassword.$error }"
          :label="$t('REGISTER.CONFIRM_PASSWORD.LABEL')"
          :placeholder="$t('REGISTER.CONFIRM_PASSWORD.PLACEHOLDER')"
          :has-error="v$.credentials.confirmPassword.$error"
          :error-message="confirmPasswordErrorText"
          @blur="v$.credentials.confirmPassword.$touch"
        />
        <div v-if="globalConfig.hCaptchaSiteKey" class="mb-3">
          <VueHcaptcha
            ref="hCaptcha"
            :class="{ error: !hasAValidCaptcha && didCaptchaReset }"
            :sitekey="globalConfig.hCaptchaSiteKey"
            @verify="onRecaptchaVerified"
          />
          <span
            v-if="!hasAValidCaptcha && didCaptchaReset"
            class="text-xs text-n-ruby-9"
          >
            {{ $t('SET_NEW_PASSWORD.CAPTCHA.ERROR') }}
          </span>
        </div>
        <NextButton
          lg
          type="submit"
          data-testid="submit_button"
          class="w-full"
          icon="i-lucide-chevron-right"
          trailing-icon
          :label="$t('REGISTER.SUBMIT')"
          :disabled="isSignupInProgress || !isFormValid"
          :is-loading="isSignupInProgress"
        />
      </div>
    </form>
    <div class="flex flex-col">
      <SimpleDivider
        v-if="showGoogleOAuth || showSamlLogin"
        :label="$t('COMMON.OR')"
        bg="bg-n-background"
        class="uppercase"
      />
      <GoogleOAuthButton v-if="showGoogleOAuth">
        {{ $t('REGISTER.OAUTH.GOOGLE_SIGNUP') }}
      </GoogleOAuthButton>
    </div>
    <p
      class="text-sm mb-1 mt-5 text-n-slate-12 [&>a]:text-woot-500 [&>a]:font-medium [&>a]:hover:text-woot-600"
      v-html="termsLink"
    />
  </div>
</template>

<style scoped lang="scss">
.h-captcha--box {
  &::v-deep .error {
    iframe {
      @apply rounded-md border border-n-ruby-8 dark:border-n-ruby-8;
    }
  }
}
</style>
